import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Images, Info } from "lucide-react";
import { openGalleryFolder } from "@/lib/projectCategories";
import { HeroBackground } from "@/components/HeroBackground";

const ROT_TOOLTIP =
  "Som privatkund kan du ha rätt till ROT-avdrag på arbetskostnaden. Kontakta oss så berättar vi mer och hjälper dig gärna vidare.";
const CARD_CLASS =
  "bg-white/10 backdrop-blur-md border border-white/20 border-l-2 border-l-[#D92525] px-4 py-4 lg:px-6 lg:py-5 lg:flex lg:items-baseline lg:gap-4";
const VALUE_CLASS =
  "font-display text-2xl md:text-3xl font-black text-[#FFFFFF] leading-none [text-shadow:0_1px_2px_rgba(11,19,43,0.6)]";
const LABEL_CLASS =
  "font-body text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#E8EAF0] mt-1.5 lg:mt-0 [text-shadow:0_1px_2px_rgba(11,19,43,0.6)]";

const RotCard = ({ index, value, label, text }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative group/rot"
      onMouseEnter={() => canHover && setOpen(true)}
      onMouseLeave={() => canHover && setOpen(false)}
    >
      <button
        type="button"
        data-testid={`hero-stat-${index}`}
        aria-expanded={open}
        aria-describedby="rot-tooltip"
        onClick={() => setOpen((v) => (canHover ? true : !v))}
        className={`${CARD_CLASS} w-full text-left cursor-pointer transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D92525]`}
      >
        <div className={VALUE_CLASS}>{value}</div>
        <div className={`${LABEL_CLASS} inline-flex items-center gap-1.5`}>
          {label}
          <Info size={12} strokeWidth={2.5} className="text-[#FFB3B3] shrink-0" />
        </div>
      </button>
      <div
        id="rot-tooltip"
        role="tooltip"
        data-testid="rot-tooltip"
        data-open={open}
        className={`absolute z-20 right-0 lg:left-0 lg:right-auto bottom-full mb-2 w-[min(18rem,calc(100vw-2.5rem))] bg-white text-[#0B132B] border-2 border-[#0B132B] shadow-[6px_6px_0_#D92525] p-4 transition-all duration-200 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-1 pointer-events-none"
        }`}
      >
        <div className="font-display font-black text-xs uppercase tracking-[0.15em] text-[#D92525]">ROT-avdrag</div>
        <p className="font-body text-sm leading-relaxed mt-1.5">{text}</p>
      </div>
    </div>
  );
};

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex items-start pt-32 md:pt-[13.5rem] pb-20 md:pb-24 overflow-hidden"
    >
      {/* Background image — clickable, opens Måleri gallery */}
      <button
        type="button"
        onClick={() => openGalleryFolder("maleri")}
        data-testid="hero-image-link"
        aria-label="Öppna Måleri-galleriet"
        className="absolute inset-0 z-0 cursor-pointer group/hero focus:outline-none"
      >
        <HeroBackground className="w-full h-full object-cover object-[60%_center] transition-transform duration-1000 group-hover/hero:scale-[1.03]" />
        <div className="absolute inset-0 bg-[#0B132B]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/45 to-transparent" />
        {/* Discrete "Se galleri" hint appearing on hover, top-right corner */}
        <span className="absolute top-24 right-5 md:top-28 md:right-10 opacity-0 group-hover/hero:opacity-100 transition-opacity inline-flex items-center gap-2 bg-white text-[#0B132B] font-display font-black text-xs md:text-sm tracking-[0.15em] uppercase px-4 py-3 shadow-[6px_6px_0_#D92525] pointer-events-none">
          <Images size={16} strokeWidth={2.4} />
          Se galleri
        </span>
      </button>


      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 reveal">
            <h1
              data-testid="hero-title"
              className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] font-black tracking-tighter leading-[0.95] [text-shadow:0_2px_6px_rgba(11,19,43,0.45)]"
            >
              Måleri, fasad
              <br />
              <span className="text-[#D92525]">&amp; renovering.</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="font-body text-[#FFFFFF] text-lg md:text-xl mt-6 max-w-2xl leading-relaxed [text-shadow:0_1px_2px_rgba(11,19,43,0.6)]"
            >
              Vi hjälper privatpersoner och företag med måleri, fasad och
              renovering – med fokus på kvalitet, noggrannhet och hållbara resultat.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("kontakt")}
                className="btn-primary group"
              >
                Få en fri offert
                <ArrowRight
                  size={18}
                  className="ml-2 transition-transform group-hover:translate-x-1"
                />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("tjanster")}
                className="btn-secondary !bg-white/0 !text-white/90 !border-white/50 hover:!border-white hover:!bg-white hover:!text-[#0B132B]"
              >
                Se våra tjänster
              </button>
            </div>

            <ul
              data-testid="hero-trust-line"
              className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 font-body text-xs md:text-sm text-[#F2F4F7] [text-shadow:0_1px_2px_rgba(11,19,43,0.6)]"
            >
              {["F-skatt", "Momsregistrerad", "ROT-avdrag"].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check size={14} strokeWidth={3} className="text-[#D92525]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Info cards */}
          <div className="lg:col-span-4 lg:self-end lg:-mb-8 grid grid-cols-3 lg:grid-cols-1 gap-3 reveal" style={{ animationDelay: "0.2s" }}>
            {[
              { value: "15+", label: "års erfarenhet" },
              { value: "F", label: "F-skatt" },
              { value: "ROT", label: "ROT-avdrag", tooltip: ROT_TOOLTIP },
            ].map((s, i) =>
              s.tooltip ? (
                <RotCard key={i} index={i} value={s.value} label={s.label} text={s.tooltip} />
              ) : (
                <div key={i} data-testid={`hero-stat-${i}`} className={CARD_CLASS}>
                  <div className={VALUE_CLASS}>{s.value}</div>
                  <div className={LABEL_CLASS}>{s.label}</div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
